// usePrizePool.ts
import { useMemo } from 'react';
import { LootProps } from '../leagues';
import { useLeague } from './useLeague';
import { useTokenSelector } from './useTokenSelector';
import { useBilling } from './useBilling';

type PrizePoolResult = Partial<Record<keyof LootProps, number>> & {
  maxLoot: number;
  tokensSold: number;
  currentLoot: number;
};

export const usePrizePool = (): PrizePoolResult => {
  const { asyncLeague, currentSeason } = useLeague();
  const { tokensSold } = useTokenSelector();
  const { calculate } = useBilling();

  return useMemo(() => {
    const loot: Partial<LootProps> = asyncLeague?.loot ?? {};
    const baseTokens = currentSeason?.baseTokens ?? 0;
    const value = currentSeason?.value ?? 0;
    const { net } = calculate(value);

    const maxLoot = baseTokens * net;
    const currentLoot = tokensSold * net;

    const result: Partial<Record<keyof LootProps, number>> = {};

    for (const key in loot) {
      const typedKey = key as keyof LootProps;
      result[typedKey] = maxLoot * (loot[typedKey] ?? 0);
    }

    return {
      ...result,
      maxLoot,
      tokensSold,
      currentLoot,
    };
  }, [
    asyncLeague?.loot,
    currentSeason?.baseTokens,
    currentSeason?.value,
    tokensSold,
    calculate,
  ]);
};
